/*
  The app shell.

  **It no longer blocks the page on the nav menu.** This used to render <></> for the
  whole site while v1 /gameInfo was in flight, so a slow or failed games call meant a
  blank page rather than a page with a menu in a loading or failed state. Chrome renders
  immediately now and AppBar owns those two states next to the query that produces them
  (§6.2, §12.20).

  The outer div is the page surface. It used to be a hardcoded #f6f6f6 painted onto `body`
  by an Emotion Global, which beat index.css and would have put every rebuilt view's
  dark-theme foreground onto light grey. It is a token now.
*/

import React from 'react';
import { useMatch } from 'react-router-dom';

import AppBar from './AppBar';
import Footer from './Footer';
import SearchCardsRow from 'components/SearchCardsRow';
import { PAGE_CONTAINER } from 'components/styles';

type LayoutPropsType = {
  children: any
};

const Layout: React.FC<LayoutPropsType> = (props: LayoutPropsType) => {
  const { children } = props;

  /*
    The full-width cert lookup band is off the home page only, and the route check has to
    live HERE rather than in HomeView.

    Layout is rendered exactly once, in App.tsx, wrapping the whole <Routes> block — so
    HomeView is a route element INSIDE these children and can never hand Layout a slot to
    fill. Layout is inside <BrowserRouter>, so useMatch is available, and this is the
    smallest thing that works. (Restructuring App.tsx into react-router layout routes
    would be the tidier shape and is deliberately out of scope.)

    The full-width statistics band that used to sit under the cert lookup is gone (Robert,
    2026-10-20): its figures are in the Population Report card at the top of HomeView now.
  */
  const isHome = useMatch('/');

  /*
    On home the whole top of the page is HomeView's now (Robert, 2026-10-20: the events
    storefront's layout, banner image beside the Population Report card, then the cert
    lookup beside the population search). It sits inside the content column, so HomeView can
    render it itself, next to the search state it shares with the games below. Off the home
    page the cert lookup is the plain full-width band under the AppBar it always was.
  */
  return (
    <div className="flex min-h-screen flex-col bg-pcg-page">
      <div>
        <AppBar />
        {isHome ? null : <SearchCardsRow />}
      </div>
      {/*
        PAGE_CONTAINER: every page is as wide as the home page now (Robert, 2026-10-20),
        like the events storefront, and the AppBar, the cert lookup band and the Footer
        share the same width so their edges line up.
      */}
      <main className="flex-1">
        <div
          className={`${PAGE_CONTAINER} py-8`}
        >
          {children}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
